<template>
    <div id="test-app-dt">
        <div class="container">
            <DataTable
                :columns="bookingColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <!-- Per Page Selector -->
                        <div class="ppv2-flex-1"></div>

                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                        </div>
                    </div>
                </template>

                <template #column-staff="{ item }">
                    <ul v-if="item.staff.length > 1">
                        <li v-for="name in item.staff" :key="name">{{ name }}</li>
                    </ul>
                    <p v-else>{{ item.staff[0] }}</p>
                </template>

                <template #column-for="{ item }">
                    <ul v-if="item.for.length > 1">
                        <li v-for="(name, index) in item.for" :key="index">
                            {{ name }}
                        </li>
                    </ul>
                    <p v-else>{{ item.for[0] }}</p>
                </template>

                <template #column-prestations="{ item }">
                    <ul v-if="item.prestations.length > 1">
                        <li v-for="name in item.prestations" :key="name">{{ name }}</li>
                    </ul>
                    <p v-else>{{ item.prestations[0] }}</p>
                </template>

                <template #column-space="{ item }">
                    <ul v-if="item.space.length > 1">
                        <li v-for="name in item.space" :key="name">{{ name }}</li>
                    </ul>
                    <p v-else>{{ item.space[0] }}</p>
                </template>

                <template #column-payment="{ item }">
                    <span :class="getStatusPaied(item.paied).class" :style="getStatusPaied(item.paied).style">
                        {{ getStatusPaied(item.paied).label }}
                    </span>
                </template>

                <template #column-status="{ item }">
                    <span
                        v-for="(status, index) in getStatus(item.cancel, item.absent, item.status)"
                        :key="index"
                        class="ppv2-my-1"
                        :class="status.class"
                        :style="status.style"
                    >
                        {{ status.label }}
                    </span>
                </template>

                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <a
                            :href="`/back-office/customer/send_sms_rappel/?user_id=${item.id_customer}&booking_id=${item.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_booking_v2_edit.actions.send_sms.title')"
                        >
                            <MessageSquare :size="18" />
                        </a>
                        <a
                            :href="`/back-office/planning?modaladd=true&fillclient=${item.id_customer}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_booking_v2_edit.actions.schedule_reservation.title')"
                        >
                            <Calendar :size="18" />
                        </a>
                        <a
                            :href="`/back-office/planning?modaladd=true&booking=${item.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_booking_v2_edit.actions.reschedule_reservations.title')"
                        >
                            <Calendar :size="18" />
                        </a>
                        <a
                            :href="`/back-office/customer/send_mail_rappel/?user_id=${item.id_customer}&booking_id=${item.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_booking_v2_edit.actions.send_email.title')"
                        >
                            <Mail :size="18" />
                        </a>
                        <a
                            href="#"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_booking_v2_edit.actions.historical.title')"
                        >
                            <History :size="18" />
                        </a>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import { MessageSquare, Calendar, Mail, History } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['user'],
    components: {
        DataTable,
        Search,
        MessageSquare,
        Calendar,
        Mail,
        History,
    },
    data() {
        return {
            // Data Table State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            showPerPageDropdown: false,
            searchTimeout: null,

            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },
            usersApiUrl: '/back-office/v2/customer/' + this.user.id + '/ajaxRDV',
            bookingColumns: [
                {
                    key: 'start',
                    label: this.$t('datatable_customer_booking_v2_edit.date'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'created_at',
                    label: this.$t('datatable_customer_booking_v2_edit.created_at'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'prestations',
                    label: this.$t('datatable_customer_booking_v2_edit.prestations'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'for',
                    label: this.$t('datatable_customer_booking_v2_edit.for'),
                    sortable: true,
                    type: 'text',
                    type: 'text',
                },
                {
                    key: 'space',
                    label: this.$t('datatable_customer_booking_v2_edit.space'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'staff',
                    label: this.$t('datatable_customer_booking_v2_edit.staff'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'status',
                    label: this.$t('datatable_customer_booking_v2_edit.status'),
                    sortable: false,
                    type: 'badge',
                },
                {
                    key: 'payment',
                    label: this.$t('datatable_customer_booking_v2_edit.payment'),
                    sortable: false,
                    type: 'badge',
                },
                {
                    key: 'actions',
                    label: this.$t('datatable_customer_booking_v2_edit.action'),
                    sortable: false,
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.usersApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        selectPerPage(value) {
            this.perPage = value
            this.showPerPageDropdown = false
            this.currentPage = 1
            this.fetchData()
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },
        onError(error) {
            console.error('Datatable error:', error)
            alert('Error loading data: ' + error.message)
        },
        getStatus(cancel, absent, status) {
            let array_status_final = []
            let status_final = {}
            if (cancel) {
                array_status_final.push({
                    label: this.$t('datatable_customer_booking_v2_edit.status_item.canceled'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-red-600 ppv2-text-red-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                })
            } else {
                switch (parseInt(status)) {
                    case 1:
                        status_final = {
                            label: this.$t('datatable_customer_booking_v2_edit.status_item.booked'),
                            class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-cyan-600 ppv2-text-cyan-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        }
                        array_status_final.push(status_final)
                        break
                    case 2:
                        status_final = {
                            label: this.$t('datatable_customer_booking_v2_edit.status_item.payed'),
                            class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        }
                        array_status_final.push(status_final)
                        break
                    case 3:
                        status_final = {
                            label: this.$t('datatable_customer_booking_v2_edit.status_item.fenced'),
                            class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-red-600 ppv2-text-red-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        }
                        array_status_final.push(status_final)
                        break
                    default:
                        status_final = { label: '-', class: 'text-muted' }
                        array_status_final.push(status_final)
                }
                switch (parseInt(absent)) {
                    case 1:
                        status_final = {
                            label: this.$t('datatable_customer_booking_v2_edit.status_item.absent'),
                            class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-orange-600 ppv2-text-orange-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        }
                        array_status_final.push(status_final)
                        break
                    case 0:
                        status_final = {
                            label: this.$t('datatable_customer_booking_v2_edit.status_item.present'),
                            class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        }
                        array_status_final.push(status_final)
                        break
                    default:
                        status_final = { label: '-', class: 'text-muted' }
                        array_status_final.push(status_final)
                }
            }
            return array_status_final
        },
        getStatusPaied(paid) {
            let status_paid = {}
            if (paid === 0) {
                status_paid = {
                    label: this.$t('datatable_customer_booking_v2_edit.status_item.pre_recorded'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-yellow-600 ppv2-text-yellow-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                }
            } else {
                status_paid = {
                    label: this.$t('datatable_customer_booking_v2_edit.status_item.ruler'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                }
            }
            return status_paid
        },
    },
}
</script>

<style scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}
</style>
